import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ArrowLeft, ShoppingCart, Search, UtensilsCrossed } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getPublicMenuPath } from '@/lib/tenant/getPublicMenuPath';
import { DEMO_PUBLIC_BRAND } from '@/lib/demo/demoBusiness';
import CustomerLanguageSwitcher from '@/components/menu/CustomerLanguageSwitcher';

export default function CatalogHeader({
  routeBid,
  bizForTheme,
  businessName,
  tableNumber = '',
  standard,
  premium,
  branding,
  tc,
  searchQuery,
  onSearchChange,
  cartCount,
  isFallbackMode,
  onCheckoutNavigate,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const headerBarStyle = premium
    ? {
        backgroundColor: branding.background_color,
        borderBottom: `1px solid ${branding.card_border_color}`,
      }
    : undefined;

  const searchInputStyle = premium
    ? {
        backgroundColor: branding.card_background_color,
        color: branding.text_color,
        borderColor: branding.card_border_color,
      }
    : undefined;

  const searchField = (
    <div className="relative">
      <Search
        className={cn(
          'absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5',
          standard ? tc.searchIcon || 'text-muted-foreground' : '',
        )}
        style={premium ? { color: branding.muted_text_color } : undefined}
      />
      <Input
        placeholder={t('catalog.searchPlaceholder')}
        value={searchQuery}
        onChange={(e) => onSearchChange(e.target.value)}
        className={cn('pl-10', standard && tc.searchInput)}
        style={searchInputStyle}
      />
    </div>
  );

  const mesaLabel = String(tableNumber || '').trim();

  const langSwitcher = (
    <CustomerLanguageSwitcher
      compact
      className="shrink-0"
      style={premium ? { color: branding.muted_text_color } : undefined}
      selectStyle={
        premium
          ? {
              borderColor: branding.card_border_color,
              backgroundColor: branding.card_background_color,
              color: branding.text_color,
            }
          : undefined
      }
    />
  );

  return (
    <>
      <div className={cn('sticky top-0 z-40', standard && tc.header)} style={headerBarStyle}>
        <div className={cn('max-w-7xl mx-auto px-4', standard ? 'py-4' : 'py-3')}>
          <div className={cn('flex items-center justify-between gap-2', standard && 'mb-4')}>
            <Button
              variant="ghost"
              onClick={() => navigate(getPublicMenuPath(routeBid, 'home'))}
              className="shrink-0"
              style={premium ? { color: branding.text_color } : undefined}
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              {t('common.back')}
            </Button>
            {standard ? (
              <div className="flex flex-1 min-w-0 flex-col items-center justify-center gap-1">
                {bizForTheme?.is_demo ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] font-medium tracking-wide border-primary/25 bg-primary/5 text-foreground/90"
                  >
                    {DEMO_PUBLIC_BRAND.badge}
                  </Badge>
                ) : null}
                <h1 className={cn('text-xl font-bold truncate text-center w-full', tc.headerTitle)}>
                  {bizForTheme?.name || businessName}
                </h1>
                {mesaLabel ? (
                  <Badge
                    variant="secondary"
                    className="text-[11px] font-medium gap-1"
                    data-testid="catalog-mesa-badge"
                  >
                    <UtensilsCrossed className="h-3 w-3" aria-hidden />
                    {t('common.table', { n: mesaLabel })}
                  </Badge>
                ) : null}
              </div>
            ) : (
              <div className="flex-1 min-w-0 flex flex-col items-center gap-1">
                {mesaLabel ? (
                  <Badge
                    variant="secondary"
                    className="text-[11px] font-medium gap-1"
                    data-testid="catalog-mesa-badge"
                  >
                    <UtensilsCrossed className="h-3 w-3" aria-hidden />
                    {t('common.table', { n: mesaLabel })}
                  </Badge>
                ) : null}
                {bizForTheme?.is_demo ? (
                  <Badge
                    variant="outline"
                    className="text-[10px] font-medium tracking-wide border-primary/25 bg-primary/5 text-foreground/90"
                  >
                    {DEMO_PUBLIC_BRAND.badge}
                  </Badge>
                ) : null}
              </div>
            )}
            <div className="flex items-center justify-end gap-2 shrink-0">
              {langSwitcher}
              {!isFallbackMode && cartCount > 0 ? (
                <Button
                  variant="outline"
                  size="sm"
                  style={
                    premium
                      ? {
                          borderColor: branding.card_border_color,
                          backgroundColor: branding.card_background_color,
                          color: branding.text_color,
                        }
                      : undefined
                  }
                  onClick={onCheckoutNavigate}
                  data-testid="catalog-header-cart"
                >
                  <ShoppingCart className="w-4 h-4 sm:mr-1" />
                  <span className="hidden sm:inline">{t('catalog.cart', { count: cartCount })}</span>
                </Button>
              ) : (
                <span className="w-2 sm:w-6" aria-hidden />
              )}
            </div>
          </div>
          {standard ? searchField : null}
        </div>
      </div>
      {premium ? <div className="max-w-7xl mx-auto px-4 pt-4 pb-1">{searchField}</div> : null}
    </>
  );
}
